import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition } from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan } from 'components/enterprise-shell';
import DocumentNumberField from 'components/common/DocumentNumberField';
import notificationService from 'services/notificationService';
import { useNavTarget } from 'contexts/NavigationContext';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useSalesInvoices, useSalesLineLedgers, useSalesServiceLedgers, useSalesStockOnHand, stockOnHandKey } from 'hooks/api/useCommercial';
import { salesInvoiceService, partyService, serviceService, voucherTypeService, taxMetaService, taxProfileService, entityAccountingConfigService, currencyService, coaLedgerService } from 'api/services';
import { DEV_BU } from 'config/devNavigation';
import TaxFieldsPanel, { validateTaxFields, type TaxFieldValues } from 'components/modules/tax/TaxFieldsPanel';
import SetupGateModal, { useSetupGate } from 'components/modules/setup/SetupGateModal';
import {
  useItemLineLookups, blankItemFieldValues, itemFieldValuesOf, ledgerSourceTag, qtyFmt,
  ItemLineDetails, ItemLineSummary, ReadOnlyLedgerValue, type ItemFields,
} from 'components/modules/shared/itemLines';
import type { SalesDocumentKind, SalesInvoiceDto, SalesInvoiceLineDto, SalesInvoiceQueryParams, CreateSalesInvoiceRequest, CreateSalesInvoiceLineRequest, VoucherTypeDto, SupplyTypeDto, TaxCalculationRequest, PartyDto, TaxFieldContextDto, DocumentTaxLineSnapshotDto } from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

const LINE_TYPE_OPTIONS: SelectOption[] = [{ value: 'SERVICE', label: 'Service' }, { value: 'REIMBURSEMENT', label: 'Reimbursement' }, { value: 'DISCOUNT', label: 'Discount' }];
// ADR-0058 §8: an invoice's SERVICE row picks a service OR an inventory item (the row turns ITEM).
// A Sales Debit Note adds value only — no item lines, so it keeps the plain list.
const LINE_TYPE_OPTIONS_WITH_ITEMS: SelectOption[] = [{ value: 'SERVICE', label: 'Service / Item' }, { value: 'REIMBURSEMENT', label: 'Reimbursement' }, { value: 'DISCOUNT', label: 'Discount' }];
/** The combined Service / Item picker's option values: "S:<serviceId>" / "I:<itemId>". */
const SERVICE_PREFIX = 'S:';
const ITEM_PREFIX = 'I:';
const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---- Document tax context (ADR-0047 §8) ----
// The jurisdiction's DOCUMENT fields (supply type from the pack, place of supply for a
// state-based regime) render through TaxFieldsPanel; the two bound fields are mirrored into
// typed state so the request carries them both ways. An entity with no jurisdiction, or one
// whose schema declares nothing, falls back to the plain supply-type picker.
const SUPPLY_BINDING = 'DOCUMENT.SUPPLY_TYPE';
const PLACE_BINDING = 'DOCUMENT.PLACE_OF_SUPPLY';
const boundKey = (ctx: TaxFieldContextDto | null, binding: string): string | null =>
  ctx?.fields.find(f => (f.binding || '').toUpperCase() === binding)?.fieldKey ?? null;
/** The values the panel renders and the request sends: extra fields plus the two bound ones. */
const mergeTaxFields = (ctx: TaxFieldContextDto | null, extra: TaxFieldValues, supplyType: string, placeOfSupply: string): TaxFieldValues => {
  const out: TaxFieldValues = { ...extra };
  const sk = boundKey(ctx, SUPPLY_BINDING); if (sk) out[sk] = supplyType;
  const pk = boundKey(ctx, PLACE_BINDING); if (pk) out[pk] = placeOfSupply;
  return out;
};
/** One line of the snapshot breakdown shown under a document line: "CGST 9% 90.00 · SGST 9% 90.00". */
/** The line keys the tax preview refused as "[ITEM_UNMAPPED] Line N": a service with no tax master under the jurisdiction. */
const unmappedLineKeys = (errors?: string[] | null): number[] =>
  (errors || []).map(e => /\[ITEM_UNMAPPED\] Line (\d+)/.exec(e)).filter(Boolean).map(m => Number(m![1]));
const snapshotSummary = (s?: DocumentTaxLineSnapshotDto | null): string | null => {
  if (!s) return null;
  const parts: string[] = [];
  if (s.classificationCode) parts.push(`HSN/SAC ${s.classificationCode}`);
  if (s.taxTreatment && s.taxTreatment !== 'TAXABLE') parts.push(s.taxTreatment === 'ZERO_RATED' ? 'Zero-rated' : 'Exempt');
  else if (s.components.length) parts.push(s.components.map(c => `${c.componentCode} ${c.ratePercentage}% ${money(c.taxAmount)}`).join(' · '));
  if (s.rateCode) parts.push(s.rateCode);
  if (s.isBackfilled) parts.push('(backfilled)');
  return parts.join('  ·  ');
};
// Add days to a yyyy-mm-dd string; returns yyyy-mm-dd. No credit days → same date.
// Computed entirely in UTC so it never shifts by a day across timezones.
const addDays = (dateStr: string, days: number): string => {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + (days || 0));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};
// One-line address from the party (skips blanks).
const formatAddress = (p: PartyDto): string =>
  [p.addressLine1, p.city, p.stateName, p.postalCode, p.countryName].filter(Boolean).join(', ');

// An invoice can be edited/deleted only while POSTED, unlocked, and not yet settled.
const editBlockReason = (r: SalesInvoiceDto): string | null => {
  if (r.docStatus !== 'POSTED') return r.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (r.isLocked) return r.lockSource || 'Locked (filed or closed period)';
  if (r.settlementStatus === 'SETTLED') return 'Fully settled';
  if (r.settlementStatus === 'PARTIAL') return 'Partially settled';
  return null;
};
const isInvoiceEditable = (r: SalesInvoiceDto): boolean => editBlockReason(r) === null;
// Status badges shown alongside the doc status (settlement + lock).
const statusBadges = (r: SalesInvoiceDto): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <span className="badge">{r.docStatus}</span>
    {r.settlementStatus === 'PARTIAL' && <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Partially Settled</span>}
    {r.settlementStatus === 'SETTLED' && <span className="badge" style={{ background: '#16a34a', color: '#fff' }}>Fully Settled</span>}
    {r.isLocked && <span className="badge" style={{ background: '#6b7280', color: '#fff' }} title={r.lockSource || 'Locked'}>🔒 Locked</span>}
  </span>
);

// The income ledger follows the masters on ITEM and service-picked SERVICE lines (read-only on the
// line); a free-text SERVICE line (no service) names its own, and must.
export const ITEM_INCOME_LEDGER_MISSING = 'Sales income ledger not set — Entity Accounting Config → Ledger Defaults (or a Sales income override in Inventory Settings)';
export const SERVICE_INCOME_LEDGER_MISSING = 'Sales income ledger not set — Entity Accounting Config → Ledger Defaults (or a Sales income override in Service Master)';
/** Where a service's income ledger came from, as the line says it. */
export const serviceLedgerSourceTag = (source?: string | null): string =>
  source === 'SERVICE' ? 'service' : source === 'CATEGORY' ? 'service category' : source === 'DEFAULT' ? 'default' : '';
/** The resolved ledger a read-only line shows: undefined = not read yet; null = nothing resolved. */
export type ResolvedLineLedger = { ledgerId?: string | null; ledgerCode?: string | null; ledgerName?: string | null; tag?: string } | null | undefined;

/**
 * An entry row. ITEM rows (ADR-0058 §8) carry the item half. `incomeLedgerId` is used only by a
 * free-text SERVICE row (no service picked) — the ledger it must name; other rows never send one.
 */
interface LineRow extends ItemFields { key: number; lineType: string; serviceId: string; description: string; quantity: string; rate: string; lineAmount: string; incomeLedgerId: string; }
const blankLine = (key: number): LineRow => ({ key, lineType: 'SERVICE', serviceId: '', description: '', quantity: '', rate: '', lineAmount: '', incomeLedgerId: '', ...blankItemFieldValues() });
/** A line that goes to the server: an amount, or an item picked (its errors then surface). */
const isSubmitted = (l: LineRow) => parseFloat(l.lineAmount) > 0 || (l.lineType === 'ITEM' && !!l.itemId);

// ---- Entry modal ----
interface InvoiceFormModalProps {
  isOpen: boolean;
  /** 'create' posts a new invoice; 'edit' alters an existing unlocked one;
   *  'copy' seeds the form from an existing invoice and posts a NEW independent one. */
  mode?: 'create' | 'edit' | 'copy';
  /** Edit: the invoice being edited. Copy: the source invoice to seed from. */
  invoice?: SalesInvoiceDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, invoiceNo?: string, kind?: SalesDocumentKind) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
  /**
   * Create: which document to raise (ADR-0053). DEBIT_NOTE raises a Sales Debit Note — the
   * same form, debit-note voucher types, an optional invoice it revises and a reason.
   * Edit takes the kind from the document itself.
   */
  documentKind?: SalesDocumentKind;
  /** Create a debit note: the invoice it revises — seeds the customer, tax context and currency. */
  revises?: SalesInvoiceDto | null;
}
export const InvoiceFormModal: React.FC<InvoiceFormModalProps> = ({ isOpen, mode = 'create', invoice, onSaved, onClose, tabId, moduleName, createInstanceId, copyInstanceId, onRestoreModal, minimizeRef, documentKind = 'INVOICE', revises }) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  /** Default posting date: today when it falls in the selected financial year, else the year's last (or first) day. */
  const docToday = () => defaultDateIn(fyRef.current);
  const { minimize } = useModalMinimize();
  const isRestoreRef = useRef(false);
  const invoiceRef = useRef(invoice); invoiceRef.current = invoice;
  const revisesRef = useRef(revises); revisesRef.current = revises;
  const modeRef = useRef(mode); modeRef.current = mode;
  const kind: SalesDocumentKind = mode === 'edit' && invoice ? (invoice.documentKind || 'INVOICE') : documentKind;
  const isDebitNote = kind === 'DEBIT_NOTE';
  const kindRef = useRef(kind); kindRef.current = kind;
  const docLabel = isDebitNote ? 'Debit Note' : 'Invoice';
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId); copyInstanceIdRef.current = copyInstanceId;
  const isCopy = mode === 'copy';
  const [editId, setEditId] = useState<string | null>(null);
  const [editRowVersion, setEditRowVersion] = useState(0);
  const [voucherTypeId, setVoucherTypeId] = useState('');
  const [partyId, setPartyId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const dueDateManual = useRef(false);
  const [attentionTo, setAttentionTo] = useState('');
  const [billTo, setBillTo] = useState<PartyDto | null>(null);
  // Role tagging in the picker (party+role): default = customer-tagged parties only (CUSTOMER or
  // BOTH); "search all" widens, and picking a supplier-only party offers a one-click promote to BOTH.
  const [searchAllParties, setSearchAllParties] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [supplyType, setSupplyType] = useState('STANDARD');
  const [placeOfSupplyCode, setPlaceOfSupplyCode] = useState('');
  const [taxFields, setTaxFields] = useState<TaxFieldValues>({});
  const [taxContext, setTaxContext] = useState<TaxFieldContextDto | null>(null);
  const [currencyCode, setCurrencyCode] = useState('');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [narration, setNarration] = useState('');
  // Debit note only (ADR-0053).
  const [againstInvoiceId, setAgainstInvoiceId] = useState('');
  const [noteReason, setNoteReason] = useState('');
  const againstLabelCache = useRef<Map<string, string>>(new Map());
  const [lines, setLines] = useState<LineRow[]>([blankLine(1)]);
  const lineKey = useRef(1);

  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeDto[]>([]);
  const [supplyTypes, setSupplyTypes] = useState<SupplyTypeDto[]>([]);
  const partyCache = useRef<Map<string, { label: string; currency?: string | null; partyType?: string }>>(new Map());
  const currencyLabelCache = useRef<Map<string, string>>(new Map());
  const serviceCache = useRef<Map<string, { name: string; rate?: number | null; desc?: string | null; incomeLedgerId?: string | null }>>(new Map());
  const ledgerCache = useRef<Map<string, string>>(new Map());
  // Item lines (ADR-0058 §8): invoices only — items not blocked for sale, saleable locations of this BU.
  const allowItems = !isDebitNote;
  const itemLookups = useItemLineLookups('SALE');
  const loadItemDetailsRef = useRef(itemLookups.loadItemDetails); loadItemDetailsRef.current = itemLookups.loadItemDetails;

  const [tax, setTax] = useState<{ taxable: number; tax: number; grand: number } | null>(null);
  /** Why tax is absent, when it is. Null when tax calculated normally (including a genuine zero). */
  const [taxNote, setTaxNote] = useState<string | null>(null);
  const [taxByKey, setTaxByKey] = useState<Record<number, { tax: number; total: number }>>({});
  // Lines whose service has no tax master: flagged on the line and Save is refused (the post
  // would refuse them too). The default master is a pre-fill, so this is the real guard.
  const [unmappedKeys, setUnmappedKeys] = useState<number[]>([]);
  const [descFocusKey, setDescFocusKey] = useState<number | null>(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialSnapshotRef = useRef<string>('');

  // FK catalog loader — refreshed at 3 sites (mount, save success, restore) so
  // cross-tab creations of voucher types or supply types show up here.
  const loadLookups = useCallback(async () => {
    try {
      // A debit note numbers from the debit-note series only; an invoice never from them (ADR-0053).
      const [vt, st, ctx] = await Promise.all([
        voucherTypeService.getAll({ voucherClass: 'SALES_INVOICE', documentKind: kindRef.current === 'DEBIT_NOTE' ? 'DEBIT_NOTE' : 'DEFAULT', pageSize: 100 }),
        taxMetaService.getSupplyTypes(),
        taxMetaService.getFieldContext('DOCUMENT').catch(() => null),
      ]);
      setVoucherTypes(vt.data);
      // One debit-note series to choose from — choose it, and count it as the starting state.
      if (kindRef.current === 'DEBIT_NOTE' && vt.data.length === 1 && modeRef.current !== 'edit') {
        const only = vt.data[0].id;
        setVoucherTypeId(prev => {
          if (prev) return prev;
          try {
            const snap = JSON.parse(initialSnapshotRef.current);
            if (!snap.voucherTypeId) { snap.voucherTypeId = only; initialSnapshotRef.current = JSON.stringify(snap); }
          } catch { /* snapshot not initialised yet */ }
          return only;
        });
      }
      setSupplyTypes(st);
      setTaxContext(ctx);
    } catch {
      // Individual dropdowns will just render as empty; the user can retry via Refresh.
      setVoucherTypes([]); setSupplyTypes([]);
    }
  }, []);
  useEffect(() => { if (isOpen) loadLookups(); }, [isOpen, loadLookups]);

  /** A saved line as an entry row (edit / copy) — ITEM lines keep their item half; a free-text SERVICE line its ledger. */
  const rowFromLine = (l: SalesInvoiceLineDto, key: number): LineRow => {
    if (l.serviceId) serviceCache.current.set(l.serviceId, { name: l.serviceName || '', rate: l.rate, desc: l.description, incomeLedgerId: l.incomeLedgerId });
    const isItem = l.lineType === 'ITEM';
    const isFreeText = l.lineType === 'SERVICE' && !l.serviceId;
    if (isFreeText && l.incomeLedgerId) ledgerCache.current.set(l.incomeLedgerId, `${l.incomeLedgerCode || ''} - ${l.incomeLedgerName || ''}`);
    return {
      key, lineType: l.lineType, serviceId: l.serviceId || '', description: l.description || '', quantity: l.quantity != null ? String(l.quantity) : '', rate: l.rate != null ? String(l.rate) : '', lineAmount: String(l.lineAmount),
      incomeLedgerId: isFreeText ? (l.incomeLedgerId || '') : '',
      ...(isItem ? itemFieldValuesOf(l) : blankItemFieldValues()),
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    lineKey.current = 1; dueDateManual.current = false;
    setTax(null); setTaxByKey({}); setErrors({}); setHeaderCollapsed(false); setMaximized(false); setSearchAllParties(false);

    const inv = invoiceRef.current;
    // Packs + item types for item lines already on the document (best-effort).
    if ((modeRef.current === 'edit' || modeRef.current === 'copy') && inv) {
      void loadItemDetailsRef.current((inv.lines || []).filter(l => l.lineType === 'ITEM' && l.itemId).map(l => l.itemId!));
    }
    if (modeRef.current === 'edit' && inv) {
      // Prefill from the existing invoice (lines come pre-loaded via getById).
      const rows: LineRow[] = (inv.lines || []).map((l, idx) => rowFromLine(l, idx + 1));
      lineKey.current = Math.max(1, rows.length);
      const linesToSet = rows.length ? rows : [blankLine(1)];
      setEditId(inv.id); setEditRowVersion(inv.rowVersion);
      setVoucherTypeId(inv.voucherTypeId || '');
      setPartyId(inv.partyId); partyCache.current.set(inv.partyId, { label: inv.partyName || '', currency: inv.currencyCode });
      setInvoiceDate((inv.invoiceDate || '').slice(0, 10));
      setDueDate((inv.dueDate || '').slice(0, 10)); dueDateManual.current = true;
      setAttentionTo(inv.attentionTo || ''); setSupplyType(inv.supplyType || '');
      setPlaceOfSupplyCode(inv.placeOfSupplyCode || ''); setTaxFields({ ...(inv.taxAttributes || {}) });
      setCurrencyCode(inv.currencyCode || ''); setExchangeRate(String(inv.exchangeRate ?? 1));
      setManualNumber(inv.invoiceNumber || ''); setNarration(inv.narration || '');
      setAgainstInvoiceId(inv.againstInvoiceId || ''); setNoteReason(inv.noteReason || '');
      if (inv.againstInvoiceId) againstLabelCache.current.set(inv.againstInvoiceId, inv.againstInvoiceNumber || '');
      setLines(linesToSet);
      // Snapshot for isDirty tracking — sync fields only; billTo / functionalCurrency
      // are read-only side data and not user-editable, so excluded.
      initialSnapshotRef.current = JSON.stringify({
        againstInvoiceId: inv.againstInvoiceId || '', noteReason: inv.noteReason || '',
        voucherTypeId: inv.voucherTypeId || '',
        partyId: inv.partyId,
        invoiceDate: (inv.invoiceDate || '').slice(0, 10),
        dueDate: (inv.dueDate || '').slice(0, 10),
        attentionTo: inv.attentionTo || '',
        supplyType: inv.supplyType || '',
        placeOfSupplyCode: inv.placeOfSupplyCode || '', taxFields: { ...(inv.taxAttributes || {}) },
        currencyCode: inv.currencyCode || '',
        exchangeRate: String(inv.exchangeRate ?? 1),
        manualNumber: inv.invoiceNumber || '',
        narration: inv.narration || '',
        lines: linesToSet,
      });
      entityAccountingConfigService.getConfig().then(c => setFunctionalCurrency(c.functionalCurrencyCode || '')).catch(() => {});
      partyService.getById(inv.partyId).then(setBillTo).catch(() => setBillTo(null));
    } else if (modeRef.current === 'copy' && inv) {
      // Copy: seed a NEW invoice from an existing one. `editId` MUST stay null
      // so `handleConfirmSave` calls `create()`. Invoice number is blank
      // (auto-generated on post); date defaults to today.
      const rows: LineRow[] = (inv.lines || []).map((l, idx) => rowFromLine(l, idx + 1));
      lineKey.current = Math.max(1, rows.length);
      const linesToSet = rows.length ? rows : [blankLine(1)];
      setEditId(null); setEditRowVersion(0);
      setVoucherTypeId(inv.voucherTypeId || '');
      setPartyId(inv.partyId); partyCache.current.set(inv.partyId, { label: inv.partyName || '', currency: inv.currencyCode });
      setInvoiceDate(docToday());
      // Recompute the due date from today + party's credit days once billTo loads.
      setDueDate(''); dueDateManual.current = false;
      setAttentionTo(inv.attentionTo || ''); setSupplyType(inv.supplyType || '');
      setPlaceOfSupplyCode(inv.placeOfSupplyCode || ''); setTaxFields({ ...(inv.taxAttributes || {}) });
      setCurrencyCode(inv.currencyCode || ''); setExchangeRate(String(inv.exchangeRate ?? 1));
      setManualNumber(''); setNarration(inv.narration || '');
      setAgainstInvoiceId(''); setNoteReason('');
      setLines(linesToSet);
      initialSnapshotRef.current = JSON.stringify({
        againstInvoiceId: '', noteReason: '',
        voucherTypeId: inv.voucherTypeId || '',
        partyId: inv.partyId,
        invoiceDate: docToday(),
        dueDate: '',
        attentionTo: inv.attentionTo || '',
        supplyType: inv.supplyType || '',
        placeOfSupplyCode: inv.placeOfSupplyCode || '', taxFields: { ...(inv.taxAttributes || {}) },
        currencyCode: inv.currencyCode || '',
        exchangeRate: String(inv.exchangeRate ?? 1),
        manualNumber: '',
        narration: inv.narration || '',
        lines: linesToSet,
      });
      entityAccountingConfigService.getConfig().then(c => setFunctionalCurrency(c.functionalCurrencyCode || '')).catch(() => {});
      partyService.getById(inv.partyId).then(p => {
        setBillTo(p);
        if (!dueDateManual.current) {
          const d = addDays(today(), p.creditDays || 0);
          setDueDate(d);
          try {
            const snap = JSON.parse(initialSnapshotRef.current);
            snap.dueDate = d;
            initialSnapshotRef.current = JSON.stringify(snap);
          } catch { /* snapshot may not be initialized on restore */ }
        }
      }).catch(() => setBillTo(null));
    } else {
      const createLines = [blankLine(1)];
      // A debit note raised from an invoice starts from that invoice's customer, tax context and
      // currency (ADR-0053) — the lines are the NEW value, so they start blank. The rate is a
      // starting point only: the note is new value at its own date and rate.
      const src = kindRef.current === 'DEBIT_NOTE' ? revisesRef.current : null;
      setEditId(null); setEditRowVersion(0);
      setVoucherTypeId(''); setPartyId(src?.partyId || ''); setInvoiceDate(docToday()); setDueDate(''); setAttentionTo(src?.attentionTo || ''); setBillTo(null);
      setSupplyType(src?.supplyType || 'STANDARD');
      setPlaceOfSupplyCode(src?.placeOfSupplyCode || ''); setTaxFields({ ...(src?.taxAttributes || {}) });
      setCurrencyCode(src?.currencyCode || ''); setExchangeRate(String(src?.exchangeRate ?? 1)); setManualNumber(''); setNarration(''); setLines(createLines);
      setAgainstInvoiceId(src?.id || ''); setNoteReason('');
      if (src) {
        partyCache.current.set(src.partyId, { label: src.partyName || '', currency: src.currencyCode });
        againstLabelCache.current.set(src.id, src.invoiceNumber || '');
        partyService.getById(src.partyId).then(setBillTo).catch(() => setBillTo(null));
      }
      initialSnapshotRef.current = JSON.stringify({
        againstInvoiceId: src?.id || '', noteReason: '',
        voucherTypeId: '', partyId: src?.partyId || '', invoiceDate: docToday(), dueDate: '',
        attentionTo: src?.attentionTo || '', supplyType: src?.supplyType || 'STANDARD', placeOfSupplyCode: src?.placeOfSupplyCode || '',
        taxFields: { ...(src?.taxAttributes || {}) }, currencyCode: src?.currencyCode || '', exchangeRate: String(src?.exchangeRate ?? 1),
        manualNumber: '', narration: '', lines: createLines,
      });
      entityAccountingConfigService.getConfig().then(c => {
        const fc = c.functionalCurrencyCode || '';
        setFunctionalCurrency(fc);
        setCurrencyCode(prev => {
          const next = prev || fc;
          // Rewrite the snapshot to include the async-loaded default so
          // "no user action yet" doesn't register as dirty.
          try {
            const snap = JSON.parse(initialSnapshotRef.current);
            snap.currencyCode = next;
            initialSnapshotRef.current = JSON.stringify(snap);
          } catch { /* snapshot ref might be empty during a restore path */ }
          return next;
        });
      }).catch(() => {});
    }
  }, [isOpen]);

  const selectedVt = voucherTypes.find(v => v.id === voucherTypeId);
  const showManualNumber = !!selectedVt && (selectedVt.numberingMethod === 'MANUAL' || selectedVt.allowManualEntry);
  // Supply types come solely from tax.tbl_supply_type via taxMetaService.
  // No hardcoded fallback — if the tax master isn't seeded, the dropdown is
  // empty and the user must set it up in Centrix / Tax Settings before
  // posting an invoice.
  const supplyTypeOptions: SelectOption[] = supplyTypes.map(s => ({ value: s.supplyCode, label: s.supplyName }));

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({
        pageSize: 50, searchTerm: input || undefined,
        // Customer-tagged by default (CUSTOMER or BOTH — the server widens BOTH itself).
        partyType: searchAllParties ? undefined : 'CUSTOMER',
      });
      r.data.forEach(p => partyCache.current.set(p.id, { label: `${p.partyCode} - ${p.partyName}`, currency: p.currencyCode, partyType: p.partyType }));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}${p.partyType === 'SUPPLIER' ? '  (supplier)' : ''}` }));
    } catch {
      return [];
    }
  }, [searchAllParties]);
  // Debit note: the customer's posted invoices it may revise (never another debit note).
  const loadRevisable = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await salesInvoiceService.getAll({ documentKind: 'INVOICE', partyId: partyId || undefined, searchTerm: input || undefined, pageSize: 50 });
      return r.data.map(i => {
        const label = `${i.invoiceNumber} · ${(i.invoiceDate || '').slice(0, 10)} · ${i.currencyCode} ${money(i.grandTotal)}${partyId ? '' : ` · ${i.partyName}`}`;
        againstLabelCache.current.set(i.id, label);
        return { value: i.id, label };
      });
    } catch {
      return [];
    }
  }, [partyId]);
  /** Picking the invoice a note revises carries its customer, tax context and currency over. */
  const onPickRevised = (id: string) => {
    setAgainstInvoiceId(id);
    setErrors(prev => { const { againstInvoiceId: _a, ...rest } = prev; return rest; });
    if (!id) return;
    salesInvoiceService.getById(id).then(src => {
      if (src.partyId !== partyId) {
        partyCache.current.set(src.partyId, { label: src.partyName || '', currency: src.currencyCode });
        setPartyId(src.partyId);
        partyService.getById(src.partyId).then(p => {
          setBillTo(p);
          if (!dueDateManual.current) setDueDate(addDays(invoiceDate, p.creditDays || 0));
        }).catch(() => setBillTo(null));
      }
      setSupplyType(src.supplyType || 'STANDARD');
      setPlaceOfSupplyCode(src.placeOfSupplyCode || '');
      setTaxFields({ ...(src.taxAttributes || {}) });
      if (src.currencyCode && src.currencyCode !== currencyCode) {
        setCurrencyCode(src.currencyCode);
        setExchangeRate(src.currencyCode === functionalCurrency ? '1' : String(src.exchangeRate ?? ''));
      }
    }).catch(() => notificationService.toast.error('Could not load the invoice being revised.'));
  };
  const loadCurrencies = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      return list.map(c => {
        const label = `${c.currencyCode} — ${c.currencyName}`;
        currencyLabelCache.current.set(c.currencyCode, label);
        return { value: c.currencyCode, label };
      });
    } catch {
      return [];
    }
  }, []);

  /**
   * Changing the currency resets the rate rather than carrying the old one over: a rate left
   * behind from a previous currency would restate every amount at a number that means nothing.
   * Home currency is always exactly 1 — the server refuses anything else.
   */
  const onPickCurrency = (code: string) => {
    setCurrencyCode(code);
    setExchangeRate(code === functionalCurrency ? '1' : '');
    setErrors(prev => { const { currencyCode: _c, exchangeRate: _r, ...rest } = prev; return rest; });
  };

  const onPickParty = (id: string) => {
    setPartyId(id);
    // The invoice a debit note revises belongs to one customer; another customer drops it.
    if (isDebitNote && againstInvoiceId && id !== partyId) setAgainstInvoiceId('');
    // The client seeds the currency; it does not lock it. Picking a client after choosing a
    // currency would otherwise silently undo that choice.
    const cur = partyCache.current.get(id)?.currency || functionalCurrency;
    setCurrencyCode(cur);
    // A state-based jurisdiction defaults the place of supply to the buyer's state (ADR-0047 §8,
    // Slice 4); the document may still override it. Only when the schema declares the field and
    // the user has not chosen one.
    // A buyer with no state on record takes the supplier's place — the issuing registration's
    // state, i.e. this business unit's (the general rule for a recipient with no location).
    if (id && taxContext && boundKey(taxContext, PLACE_BINDING) && !placeOfSupplyCode) {
      const supplier = taxContext.supplierStateCode || '';
      taxProfileService.getPartyProfile(id)
        .then(p => setPlaceOfSupplyCode(prev => prev || p?.taxStateCode || supplier))
        .catch(() => setPlaceOfSupplyCode(prev => prev || supplier));
    }
    if (cur === functionalCurrency) setExchangeRate('1');
    if (!id) { setBillTo(null); return; }
    // Fetch full party for the Bill-To panel + credit-days due-date default.
    partyService.getById(id).then(p => {
      setBillTo(p);
      if (!dueDateManual.current) setDueDate(addDays(invoiceDate, p.creditDays || 0));
    }).catch(() => setBillTo(null));
  };
  /** Tag a supplier as customer too (party_type → BOTH) so it lands in the customer picker for good. */
  const promoteToCustomer = async () => {
    if (!billTo) return;
    setPromoting(true);
    try {
      await partyService.update(billTo.id, {
        partyCode: billTo.partyCode, partyName: billTo.partyName, legalName: billTo.legalName,
        partyType: 'BOTH', partyGroupId: billTo.partyGroupId,
        email: billTo.email, phone: billTo.phone,
        countryId: billTo.countryId, countryName: billTo.countryName, stateName: billTo.stateName,
        addressLine1: billTo.addressLine1, city: billTo.city, postalCode: billTo.postalCode,
        currencyCode: billTo.currencyCode, creditDays: billTo.creditDays, notes: billTo.notes,
        statusCode: billTo.statusCode || 'ACTIVE',
        rowVersion: billTo.rowVersion,
      });
      const refreshed = await partyService.getById(billTo.id);
      setBillTo(refreshed);
      partyCache.current.set(refreshed.id, { label: `${refreshed.partyCode} - ${refreshed.partyName}`, currency: refreshed.currencyCode, partyType: refreshed.partyType });
      notificationService.toast.success(`${refreshed.partyName} is now tagged as a customer.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not tag the party as a customer.');
    } finally { setPromoting(false); }
  };
  const onChangeInvoiceDate = (d: string) => {
    setInvoiceDate(d);
    if (!dueDateManual.current) setDueDate(addDays(d, billTo?.creditDays || 0));   // credit days, else same date
  };
  const loadServices = useCallback(async (input: string, itemType: string): Promise<SelectOption[]> => {
    try {
      const r = await serviceService.getAll({ itemType, pageSize: 50, searchTerm: input || undefined });
      r.data.forEach(s => serviceCache.current.set(s.id, { name: `${s.serviceCode} - ${s.serviceName}`, rate: s.defaultRate, desc: s.description, incomeLedgerId: s.incomeLedgerId }));
      return r.data.map(s => ({ value: s.id, label: `${s.serviceCode} - ${s.serviceName}` }));
    } catch {
      return [];
    }
  }, []);

  const updLine = (key: number, patch: Partial<LineRow>) => setLines(rows => rows.map(r => {
    if (r.key !== key) return r;
    const n = { ...r, ...patch };
    if (patch.quantity !== undefined || patch.rate !== undefined) {
      const q = parseFloat(n.quantity), rt = parseFloat(n.rate);
      if (!isNaN(q) && !isNaN(rt)) n.lineAmount = (q * rt).toFixed(2);
    }
    return n;
  }));
  const onPickService = (key: number, sid: string) => {
    const meta = serviceCache.current.get(sid);
    // A picked service's ledger follows the Service Master — any free-text ledger pick goes.
    updLine(key, { serviceId: sid, incomeLedgerId: '', description: meta?.desc || meta?.name || '', rate: meta?.rate != null ? String(meta.rate) : '' });
    if (!sid) return;
    // Default the line description to the current BU's Invoice Default Description; fall back to the service description.
    serviceService.getById(sid).then(full => {
      const bu = (full.buDescriptions || []).find(b => b.businessUnitId === DEV_BU.id);
      const desc = bu?.description || full.description || `${full.serviceCode} - ${full.serviceName}`;
      updLine(key, { description: desc });
    }).catch(() => {});
  };
  /** The Service / Item picker: services (as today) and items not blocked for sale, tagged. */
  const { loadItems } = itemLookups;
  const loadServicesAndItems = useCallback(async (input: string): Promise<SelectOption[]> => {
    const [services, items] = await Promise.all([loadServices(input, 'SERVICE'), loadItems(input)]);
    return [
      ...services.map(o => ({ value: `${SERVICE_PREFIX}${o.value}`, label: `Service · ${o.label}` })),
      ...items.map(o => ({ value: `${ITEM_PREFIX}${o.value}`, label: `Item · ${o.label}` })),
    ];
  }, [loadServices, loadItems]);
  /**
   * A pick from the Service / Item picker. A service keeps today's behaviour (SERVICE row); an
   * item turns the row ITEM, clears the service and its price, and defaults the description to
   * the item's name once the item's read lands.
   */
  const onPickProduct = (row: LineRow, value: string) => {
    if (value.startsWith(ITEM_PREFIX)) {
      const itemId = value.slice(ITEM_PREFIX.length);
      if (row.lineType === 'ITEM' && row.itemId === itemId) return;
      updLine(row.key, { lineType: 'ITEM', serviceId: '', incomeLedgerId: '', description: '', rate: '', lineAmount: '' });
      void itemLookups.pickItem(row.key, itemId, setLines).then(() => setLines(rows => rows.map(r =>
        r.key === row.key && r.itemId === itemId && !r.description.trim() ? { ...r, description: r.itemName } : r)));
      return;
    }
    const sid = value.startsWith(SERVICE_PREFIX) ? value.slice(SERVICE_PREFIX.length) : '';
    if (row.lineType === 'ITEM') updLine(row.key, { lineType: 'SERVICE', incomeLedgerId: '', ...blankItemFieldValues() });
    onPickService(row.key, sid);
  };
  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch { return []; }
  }, []);
  const addLine = () => setLines(rows => [...rows, blankLine(++lineKey.current)]);
  const removeLine = (key: number) => setLines(rows => rows.length > 1 ? rows.filter(r => r.key !== key) : rows);

  // Item lines count with services (the server's subtotal: SERVICE + ITEM).
  const subtotal = lines.filter(l => l.lineType === 'SERVICE' || l.lineType === 'ITEM').reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const hasItemLines = lines.some(l => l.lineType === 'ITEM');
  const reimbursementTotal = lines.filter(l => l.lineType === 'REIMBURSEMENT').reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const discountTotal = lines.filter(l => l.lineType === 'DISCOUNT').reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const taxableNet = subtotal + reimbursementTotal - discountTotal;

  // ---- Income ledger (read-only on ITEM and service-picked lines) + on-hand hint (ADR-0058 §8) ----
  const lineLedgers = useSalesLineLedgers('INVOICE', invoiceDate, allowItems ? lines.filter(l => l.lineType === 'ITEM').map(l => l.itemId) : [], isOpen && allowItems);
  const serviceLedgers = useSalesServiceLedgers('INVOICE', lines.filter(l => l.lineType === 'SERVICE').map(l => l.serviceId), isOpen);
  /**
   * The income ledger an ITEM line or a service-picked SERVICE line posts to, as the masters
   * resolve it (undefined = not read yet; null = nothing resolved). Other lines: undefined.
   */
  const resolvedLedgerOf = (l: LineRow): ResolvedLineLedger => {
    if (allowItems && l.lineType === 'ITEM' && l.itemId) {
      const info = lineLedgers[l.itemId];
      if (!info) return undefined;
      return info.ledgerId ? { ledgerId: info.ledgerId, ledgerCode: info.ledgerCode, ledgerName: info.ledgerName, tag: ledgerSourceTag(info) } : null;
    }
    if (l.lineType === 'SERVICE' && l.serviceId) {
      const info = serviceLedgers[l.serviceId];
      if (!info) return undefined;
      return info.ledgerId ? { ledgerId: info.ledgerId, ledgerCode: info.ledgerCode, ledgerName: info.ledgerName, tag: serviceLedgerSourceTag(info.source) } : null;
    }
    return undefined;
  };
  /** A SERVICE line with no service picked — it names its own income ledger. */
  const isFreeTextService = (l: LineRow) => l.lineType === 'SERVICE' && !l.serviceId;
  const stockRows = allowItems ? lines.filter(l => l.lineType === 'ITEM' && l.itemId && l.stockLocationId && itemLookups.itemTypeOf(l) !== 'NON_STOCK') : [];
  const onHand = useSalesStockOnHand(stockRows.map(l => ({ itemId: l.itemId, stockLocationId: l.stockLocationId })), invoiceDate, isOpen && allowItems);
  // Editing: the stored invoice already issued its own lines, so they count as available again.
  const ownIssued = useMemo(() => {
    const m: Record<string, number> = {};
    if (mode === 'edit' && invoice) {
      (invoice.lines || []).forEach(l => {
        if (l.lineType !== 'ITEM' || !l.itemId || !l.stockLocationId) return;
        const k = stockOnHandKey(l.itemId, l.stockLocationId);
        m[k] = (m[k] || 0) + (l.baseQuantity ?? 0);
      });
    }
    return m;
  }, [mode, invoice]);
  /** Base quantity the form asks of each item × location (all its lines together). */
  const demand: Record<string, number> = {};
  stockRows.forEach(l => {
    const k = stockOnHandKey(l.itemId, l.stockLocationId);
    demand[k] = (demand[k] || 0) + (parseFloat(l.quantity) || 0) * (l.factorToBase || 1);
  });
  const onHandHint = (l: LineRow): React.ReactNode => {
    if (!stockRows.includes(l)) return null;
    const k = stockOnHandKey(l.itemId, l.stockLocationId);
    if (onHand[k] === undefined) return null;
    const available = onHand[k] + (ownIssued[k] || 0);
    const short = (demand[k] || 0) - available;
    const unit = itemLookups.baseUomOf(l);
    return (
      <div style={{ fontSize: '0.66rem', marginTop: '0.15rem', color: short > 0.0000005 ? '#d97706' : 'var(--text-muted)' }}
        title={`On hand at this location on ${invoiceDate}${ownIssued[k] ? ', counting back what this invoice already issued' : ''}`}>
        On hand: {qtyFmt(available)}{unit ? ` ${unit}` : ''}{short > 0.0000005 ? ` · Short by ${qtyFmt(short)}${unit ? ` ${unit}` : ''}` : ''}
      </div>
    );
  };

  // Live tax preview (debounced).
  const taxKey = JSON.stringify({ supplyType, placeOfSupplyCode, taxFields, invoiceDate, lines: lines.map(l => ({ t: l.lineType, s: l.serviceId, i: l.itemId, a: l.lineAmount, q: l.quantity })) });
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(async () => {
      const reqLines = lines.filter(l => parseFloat(l.lineAmount) > 0).map(l => ({
        lineId: String(l.key),
        // An ITEM line is priced from the item's tax mapping, as a SERVICE line is from the service's.
        ...(l.lineType === 'ITEM' ? { serviceId: null, itemId: l.itemId || null } : { serviceId: l.serviceId || null }),
        taxableAmount: l.lineType === 'DISCOUNT' ? -(parseFloat(l.lineAmount) || 0) : (parseFloat(l.lineAmount) || 0),
        quantity: l.quantity ? parseFloat(l.quantity) : null,
        // A non-DISCOUNT line with an Amount but no Item picked yet can't
        // resolve a service-tax mapping — the backend would fall back to
        // the standard rate and show phantom tax. Mark it exempt for the
        // preview so the Tax column stays 0 until the user picks an item.
        // DISCOUNT lines legitimately have no serviceId and should NOT be
        // marked exempt (they subtract from the taxable base via the
        // negative amount above).
        isExempt: l.lineType !== 'DISCOUNT' && !(l.lineType === 'ITEM' ? l.itemId : l.serviceId) ? true : undefined,
      }));
      if (reqLines.length === 0) { setTax(null); setTaxByKey({}); setUnmappedKeys([]); return; }
      try {
        const req: TaxCalculationRequest = {
          transactionDate: invoiceDate, supplyType, direction: 'OUTPUT', lines: reqLines,
          placeOfSupplyStateCode: placeOfSupplyCode || null,
          taxFields: mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode),
        };
        const res = await taxMetaService.calculate(req);
        // A failed calculation returns an EMPTY result — zeroed summary, no lines — plus the
        // reason. Reading that as an answer showed "Grand Total 0.00" on an invoice whose lines
        // clearly totalled 30,000, because the entity had no tax jurisdiction configured.
        // Treat it as "no tax available", not "no tax due", and say which.
        if (res.success === false) {
          setTax(null); setTaxByKey({});
          setTaxNote(res.errors?.[0] || 'Tax could not be calculated for this entity.');
          setUnmappedKeys(unmappedLineKeys(res.errors));
          return;
        }
        setTaxNote(null); setUnmappedKeys([]);
        setTax({ taxable: res.summary.totalTaxableAmount, tax: res.summary.totalTaxAmount, grand: res.summary.grandTotal });
        const map: Record<number, { tax: number; total: number }> = {};
        (res.lines || []).forEach(ln => { map[Number(ln.lineId)] = { tax: ln.totalTaxAmount, total: ln.lineTotal }; });
        setTaxByKey(map);
      } catch {
        setTax(null); setTaxByKey({});
        setTaxNote('Tax could not be calculated — the total below excludes tax.');
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxKey, isOpen]);

  // Derived the same way the SERVER derives it on post — subtotal + reimbursements − discount
  // + tax — rather than trusting the preview's summary. That is what makes the figure on screen
  // and the figure that posts agree by construction: when tax cannot be calculated the invoice
  // still posts at its line total, so the screen must show that, not zero.
  const taxTotal = tax ? tax.tax : 0;
  const grandTotal = taxableNet + taxTotal;

  const currentSnapshot = useMemo(() => JSON.stringify({
    againstInvoiceId, noteReason,
    voucherTypeId, partyId, invoiceDate, dueDate, attentionTo, supplyType, placeOfSupplyCode, taxFields,
    currencyCode, exchangeRate, manualNumber, narration, lines,
  }), [againstInvoiceId, noteReason, voucherTypeId, partyId, invoiceDate, dueDate, attentionTo, supplyType, placeOfSupplyCode, taxFields, currencyCode, exchangeRate, manualNumber, narration, lines]);
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return currentSnapshot !== initialSnapshotRef.current;
  }, [currentSnapshot]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = () => {
    const e: Record<string, string> = {};
    if (!voucherTypeId) e.voucherTypeId = 'Voucher type is required';
    if (!partyId) e.partyId = 'Customer is required';
    if (isDebitNote && !noteReason.trim()) e.noteReason = 'Say why the value is being raised';
    if (showManualNumber && selectedVt?.numberingMethod === 'MANUAL' && !manualNumber.trim()) e.manualNumber = 'Number is required';
    if (!currencyCode) e.currencyCode = 'Currency is required';
    // A foreign invoice needs a real rate; a home-currency one must be exactly 1, which the
    // server enforces too — catching it here just explains it before the round trip.
    if (currencyCode && currencyCode !== functionalCurrency && !(parseFloat(exchangeRate) > 0)) {
      e.exchangeRate = `Enter today's rate — 1 ${currencyCode} in ${functionalCurrency}`;
    }
    const valid = lines.filter(isSubmitted);
    if (valid.length === 0) e.lines = 'Add at least one line with an amount';
    if (valid.some(l => l.lineType === 'SERVICE' && !l.serviceId && !l.description.trim())) e.lines = 'Service lines need a service or description';
    if (unmappedKeys.some(k => valid.some(l => l.key === k))) e.lines = 'A service or item on this document has no tax master. Map it (Service Master / the item\'s tax mapping) or pick another before posting.';
    // ITEM lines (ADR-0058 §8) — the server's rules, caught early.
    const lineErrors: string[] = [];
    valid.forEach((l, idx) => {
      if (l.lineType !== 'ITEM') return;
      const n = idx + 1;
      if (!l.itemId) { e[`line-${l.key}-item`] = 'Required'; lineErrors.push(`Line ${n}: pick the item`); return; }
      if (!(parseFloat(l.quantity) > 0)) { e[`line-${l.key}-quantity`] = 'Required'; lineErrors.push(`Line ${n}: enter the quantity (more than 0)`); }
      if (itemLookups.itemTypeOf(l) === 'STOCK' && !l.stockLocationId) { e[`line-${l.key}-location`] = 'Required'; lineErrors.push(`Line ${n}: pick the location the goods leave from`); }
    });
    if (lineErrors.length && !e.lines) e.lines = lineErrors[0];
    // Income ledger: ITEM and service-picked lines follow the masters — nothing resolved blocks the
    // post; a free-text SERVICE line must name its own.
    valid.forEach(l => {
      if (isFreeTextService(l)) {
        if (!l.incomeLedgerId) { e[`line-${l.key}-ledger`] = 'Required'; if (!e.lines) e.lines = 'A service line without a service needs its income ledger'; }
        return;
      }
      if (resolvedLedgerOf(l) === null) {
        e[`line-${l.key}-ledger`] = 'Required';
        if (!e.lines) e.lines = l.lineType === 'ITEM' ? ITEM_INCOME_LEDGER_MISSING : SERVICE_INCOME_LEDGER_MISSING;
      }
    });
    // The jurisdiction's DOCUMENT fields (supply type, place of supply) — same rules as the server.
    Object.entries(validateTaxFields(taxContext, mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode)))
      .forEach(([k, v]) => { e[`tax.${k}`] = v; });
    // Per-field line flags (line-<key>-…) only highlight; the toast reads a sentence.
    if (Object.keys(e).length) { setErrors(e); notificationService.toast.warning(Object.entries(e).find(([k]) => !k.startsWith('line-'))?.[1] || Object.values(e)[0]); return; }
    setErrors({}); setConfirmOpen(true);
  };
  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const payload: CreateSalesInvoiceRequest = {
        voucherTypeId, partyId, invoiceDate, dueDate: dueDate || undefined, supplyType,
        placeOfSupplyCode: placeOfSupplyCode || null,
        taxFields: mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode),
        currencyCode, exchangeRate: parseFloat(exchangeRate) || 1,
        // Only send manualNumber when the voucher type is MANUAL and the user
        // actually supplied a number. On edit of an AUTO-numbered invoice
        // (even when allowManualEntry=true), the field is pre-populated from
        // the previously-allocated auto number — sending that back looks like
        // a manual override and, per ADR-0020 "edit = cancel + re-issue",
        // collides with the still-active old row on uq_sales_invoice_number.
        // The re-issue MUST get a freshly-allocated number, so omit manualNumber
        // unless the voucher type is genuinely MANUAL-only.
        manualNumber: (selectedVt?.numberingMethod === 'MANUAL' && manualNumber.trim())
          ? manualNumber.trim()
          : undefined,
        attentionTo: attentionTo.trim() || undefined, narration: narration.trim() || undefined,
        againstInvoiceId: isDebitNote ? (againstInvoiceId || null) : undefined,
        noteReason: isDebitNote ? noteReason.trim() : undefined,
        lines: lines.filter(isSubmitted).map((l): CreateSalesInvoiceLineRequest => {
          if (allowItems && l.lineType === 'ITEM') {
            return {
              lineType: 'ITEM', itemId: l.itemId, uomId: l.uomId || undefined,
              stockLocationId: itemLookups.itemTypeOf(l) === 'NON_STOCK' ? undefined : (l.stockLocationId || undefined),
              // Blank description → the item's name.
              description: l.description.trim() || l.itemName || undefined,
              quantity: l.quantity ? parseFloat(l.quantity) : undefined, rate: l.rate ? parseFloat(l.rate) : undefined,
              lineAmount: parseFloat(l.lineAmount) || 0,
              // No incomeLedgerId — an ITEM line's ledger follows the masters (the server ignores one).
            };
          }
          return {
            lineType: l.lineType, serviceId: l.serviceId || undefined, description: l.description.trim() || undefined,
            quantity: l.quantity ? parseFloat(l.quantity) : undefined, rate: l.rate ? parseFloat(l.rate) : undefined,
            lineAmount: parseFloat(l.lineAmount) || 0,
            // A free-text SERVICE line names its own ledger; a service-picked one follows the Service Master.
            ...(isFreeTextService(l) ? { incomeLedgerId: l.incomeLedgerId || undefined } : {}),
          };
        }),
      };
      const saved = editId
        ? await salesInvoiceService.update(editId, { ...payload, rowVersion: editRowVersion })
        : await salesInvoiceService.create(payload);
      setSaving(false);
      // FK-refresh site (2 of 3): re-pull voucher types + supply types after a
      // successful post so a cross-tab creation shows in the current session.
      loadLookups();
      notificationService.toast.success(`${docLabel} ${saved.invoiceNumber || ''} ${editId ? 'updated' : 'posted'}.`);
      // Negative stock under the WARN policy posts, but says so (BLOCK refuses the save instead).
      if (saved.stockWarnings?.length) notificationService.toast.warning(`Stock goes below zero — ${saved.stockWarnings.join(' · ')}`);
      onSaved(); onClose();
    } catch (err: any) { setSaving(false); notificationService.toast.error(err?.response?.data?.message || `Could not ${editId ? 'update' : 'post'} the ${docLabel.toLowerCase()}.`); }
  };

  // Multi-instance-aware minimize. Modal IDs follow the blueprint:
  //   Create: `${tabId}-form-modal-create-${createInstanceId}`
  //   Copy:   `${tabId}-form-modal-copy-${copyInstanceId}`
  //   Edit:   `${tabId}-form-modal-edit-${invoiceNumber}`
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const editing = currentMode === 'edit';
    const copying = currentMode === 'copy';
    const cId = createInstanceIdRef.current || '1';
    const cpId = copyInstanceIdRef.current || '1';
    const sourceInvoiceNo = invoiceRef.current?.invoiceNumber;
    const snap = { editId, editRowVersion, voucherTypeId, partyId, invoiceDate, dueDate, dueDateManual: dueDateManual.current, attentionTo, billTo, supplyType, placeOfSupplyCode, taxFields: { ...taxFields }, currencyCode, exchangeRate, functionalCurrency, manualNumber, narration, againstInvoiceId, noteReason, lines: lines.map(l => ({ ...l })) };
    const savedInitialSnapshot = initialSnapshotRef.current;
    const snapKind = kindRef.current;
    // Match the isDirty semantics: form differs from the snapshot at open time.
    const currentIsDirty = savedInitialSnapshot ? currentSnapshot !== savedInitialSnapshot : false;
    // A debit-note draft gets its own id space so it never collides with an invoice draft.
    const idKind = snapKind === 'DEBIT_NOTE' ? 'dn-' : '';
    const modalId = editing
      ? `${tabId}-form-modal-edit-${idKind}${manualNumber || editId || ''}`
      : copying
      ? `${tabId}-form-modal-copy-${cpId}`
      : `${tabId}-form-modal-create-${idKind}${cId}`;
    const title = editing
      ? `Edit ${docLabel} - ${manualNumber || ''}`
      : copying
      ? `Copy Invoice - ${sourceInvoiceNo || 'draft'} (${cpId})`
      : partyId
      ? `${docLabel} - ${partyCache.current.get(partyId)?.label || ''} (${cId})`
      : `New ${docLabel} (${cId})`;
    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setEditId(snap.editId); setEditRowVersion(snap.editRowVersion);
        setVoucherTypeId(snap.voucherTypeId); setPartyId(snap.partyId); setInvoiceDate(snap.invoiceDate); setDueDate(snap.dueDate); dueDateManual.current = snap.dueDateManual;
        setAttentionTo(snap.attentionTo); setBillTo(snap.billTo);
        setSupplyType(snap.supplyType); setPlaceOfSupplyCode(snap.placeOfSupplyCode); setTaxFields(snap.taxFields); setCurrencyCode(snap.currencyCode); setExchangeRate(snap.exchangeRate); setFunctionalCurrency(snap.functionalCurrency);
        setManualNumber(snap.manualNumber); setNarration(snap.narration); setLines(snap.lines);
        setAgainstInvoiceId(snap.againstInvoiceId); setNoteReason(snap.noteReason);
        initialSnapshotRef.current = savedInitialSnapshot;
        // FK-refresh site (3 of 3): re-pull lookups on restore so any cross-tab
        // voucher-type / supply-type creation shows in the restored draft.
        loadLookups();
        onRestoreModal?.(
          editing ? 'edit' : copying ? 'copy' : 'create',
          editing ? undefined : copying ? cpId : cId,
          editing ? (snap.manualNumber || undefined) : copying ? (sourceInvoiceNo || undefined) : undefined,
          snapKind,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, editId, editRowVersion, voucherTypeId, partyId, invoiceDate, dueDate, attentionTo, billTo, supplyType, placeOfSupplyCode, taxFields, currencyCode, exchangeRate, functionalCurrency, manualNumber, narration, againstInvoiceId, noteReason, docLabel, lines, currentSnapshot, loadLookups]);

  // Expose handleMinimize so the screen can auto-minimize this modal when a new
  // Copy or Add is invoked while this one is still open.
  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className={`modal-container ${maximized ? '' : 'modal-xl'}`} style={maximized ? { width: '98vw', maxWidth: '98vw', height: '96vh', maxHeight: '96vh' } : undefined}>
        {saving && <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}><div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{editId ? `Edit ${docLabel} ${manualNumber || ''}` : isCopy ? `Copy Invoice${invoice ? ` — from ${invoice.invoiceNumber}` : ''}` : isDebitNote ? 'New Sales Debit Note' : 'New Service Invoice'}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}><button className="modal-header-btn" onClick={() => setMaximized(m => !m)} title={maximized ? 'Restore' : 'Maximize'} disabled={saving}>{maximized ? <IconSystem.windowRestore size={16} /> : <IconSystem.maximize size={16} />}</button>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          {/* Collapsible header — collapse to free up space for line entry */}
          <div onClick={() => setHeaderCollapsed(c => !c)} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHeaderCollapsed(c => !c); } }}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.15rem 0', marginBottom: headerCollapsed ? '0.4rem' : '0.5rem' }}>
            <span className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <IconSystem.chevronDown size={14} style={{ transform: headerCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.12s' }} />
              {docLabel} Details
            </span>
            {headerCollapsed && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(partyId && partyCache.current.get(partyId)?.label) || 'No customer'} · {invoiceDate}{dueDate ? ` · due ${dueDate}` : ''}{currencyCode ? ` · ${currencyCode}` : ''}
            </span>}
          </div>
          {!headerCollapsed && (<>
          <div className="form-grid-3">
            <div className="form-group"><SyutSelect label={<>Voucher Type <span className="text-danger">*</span></>} options={voucherTypes.map(v => ({ value: v.id, label: `${v.voucherCode} - ${v.voucherName}` }))} value={voucherTypeId} onChange={v => setVoucherTypeId((v as string) || '')} isClearable={false} isDisabled={!!editId} menuPortalTarget={document.body} error={errors.voucherTypeId} /></div>
            <div className="form-group">
              <SyutSelect key={String(searchAllParties)} label={<>Customer <span className="text-danger">*</span></>} isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={partyId ? [{ value: partyId, label: partyCache.current.get(partyId)?.label || '' }] : undefined}
                menuPortalTarget={document.body} value={partyId} onChange={v => onPickParty((v as string) || '')} placeholder={searchAllParties ? 'Search all parties' : 'Select customer'} error={errors.partyId} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={searchAllParties} onChange={e => setSearchAllParties(e.target.checked)} />
                Customer not listed? Search all parties
              </label>
            </div>
            <DocumentNumberField label={`${docLabel} No.`} voucherTypeId={voucherTypeId} date={invoiceDate}
              existingNumber={editId ? manualNumber : ''} manualValue={manualNumber} onManualChange={setManualNumber}
              required error={errors.manualNumber} />
            <div className="form-group"><label className="form-label">{docLabel} Date <span className="text-danger">*</span></label><FyDateInput className="form-input" value={invoiceDate} onChange={onChangeInvoiceDate} /></div>
            {isDebitNote && (
              <div className="form-group">
                <SyutSelect label="Revises Invoice" isAsync key={`revises-${partyId}`} loadOptions={loadRevisable} defaultOptions cacheOptions={false}
                  seedOptions={againstInvoiceId ? [{ value: againstInvoiceId, label: againstLabelCache.current.get(againstInvoiceId) || '' }] : undefined}
                  value={againstInvoiceId} onChange={v => onPickRevised((v as string) || '')} isClearable
                  menuPortalTarget={document.body} placeholder="Optional — the invoice this note adds to" />
              </div>
            )}
            <div className="form-group"><label className="form-label">Due Date{billTo?.creditDays ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {billTo.creditDays}d credit</span> : null}</label><input type="date" className="form-input" value={dueDate} onChange={e => { dueDateManual.current = true; setDueDate(e.target.value); }} /></div>
            {taxContext?.fields?.length ? (
              <TaxFieldsPanel context={taxContext} bare
                values={mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode)}
                errors={Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('tax.')).map(([k, v]) => [k.slice(4), v]))}
                onChange={(key, value) => {
                  setErrors(prev => { const n = { ...prev }; delete n[`tax.${key}`]; return n; });
                  const v = (value as string) || '';
                  if (key === boundKey(taxContext, SUPPLY_BINDING)) setSupplyType(v);
                  else if (key === boundKey(taxContext, PLACE_BINDING)) setPlaceOfSupplyCode(v);
                  else setTaxFields(prev => ({ ...prev, [key]: value }));
                }} />
            ) : taxContext?.registrationStatus === 'NOT_REGISTERED' ? (
              <div className="form-group"><label className="form-label">Tax</label><div className="form-input" style={{ color: 'var(--text-muted)', background: 'var(--bg-secondary, #f8fafc)' }}>Not registered — no tax on this document</div></div>
            ) : (
              <div className="form-group"><SyutSelect label="Supply Type" options={supplyTypeOptions} value={supplyType} onChange={v => setSupplyType((v as string) || '')} isClearable={false} menuPortalTarget={document.body} /></div>
            )}
            <div className="form-group"><label className="form-label">Attention To</label><input className="form-input" value={attentionTo} maxLength={200} onChange={e => setAttentionTo(e.target.value)} placeholder="Contact person (optional)" /></div>
            {/* The client's currency is a DEFAULT, not a constraint: one client can be billed in
                several currencies, each invoice keeping its own currency and rate on the open item
                (ADR-0031 §2). So this is a picker, and the client only decides where it starts. */}
            <div className="form-group" data-field="currencyCode">
              <SyutSelect
                label="Currency"
                isAsync defaultOptions cacheOptions={false}
                loadOptions={loadCurrencies}
                seedOptions={currencyCode ? [{ value: currencyCode, label: currencyLabelCache.current.get(currencyCode) || currencyCode }] : undefined}
                value={currencyCode}
                onChange={v => onPickCurrency((v as string) || '')}
                menuPortalTarget={document.body}
                isClearable={false}
                error={errors.currencyCode}
              />
              {currencyCode && billTo?.currencyCode && currencyCode !== billTo.currencyCode && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                  {billTo.partyName} is normally billed in {billTo.currencyCode}
                </div>
              )}
            </div>
            {currencyCode && currencyCode !== functionalCurrency && (
              <div className="form-group" data-field="exchangeRate">
                <label className="form-label">Exchange Rate <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>→ {functionalCurrency}</span> <span className="text-danger">*</span></label>
                <input type="number" step="0.000001" min="0"
                  className={`form-input${errors.exchangeRate ? ' form-field-error' : ''}`}
                  value={exchangeRate}
                  onChange={e => setExchangeRate(e.target.value)}
                  placeholder={`1 ${currencyCode} = ? ${functionalCurrency}`} />
                {errors.exchangeRate && <div className="form-error-message">{errors.exchangeRate}</div>}
              </div>
            )}
          </div>

          {isDebitNote && (
            <div className="form-group" style={{ marginTop: '0.5rem' }}>
              <label className="form-label">Reason <span className="text-danger">*</span></label>
              <textarea className={`form-textarea${errors.noteReason ? ' form-field-error' : ''}`} rows={2} maxLength={500}
                value={noteReason} placeholder="Why the customer's value is being raised — e.g. price revision, under-billing, extra charges"
                onChange={e => { setNoteReason(e.target.value); if (errors.noteReason) setErrors(prev => { const { noteReason: _n, ...rest } = prev; return rest; }); }} />
              {errors.noteReason && <div className="form-error-message">{errors.noteReason}</div>}
            </div>
          )}

          {/* Bill To — quick verification of who/where we're billing */}
          {billTo && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Bill To: {billTo.partyName}</span>
              {formatAddress(billTo) && <span style={{ color: 'var(--text-secondary)' }}>{formatAddress(billTo)}</span>}
              {attentionTo.trim() && <span style={{ color: 'var(--text-secondary)' }}>Attn: {attentionTo.trim()}</span>}
              {billTo.email && <span style={{ color: 'var(--text-secondary)' }}>✉ {billTo.email}</span>}
              {billTo.phone && <span style={{ color: 'var(--text-secondary)' }}>☎ {billTo.phone}</span>}
              {!formatAddress(billTo) && !billTo.email && !billTo.phone && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No address / contact on file</span>}
              {billTo.partyType === 'SUPPLIER' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Tagged supplier only</span>
                  <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} disabled={promoting} onClick={promoteToCustomer}>
                    {promoting ? 'Tagging…' : 'Also use as customer'}
                  </button>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Tags the party as customer and supplier, so it shows in the customer picker from now on.</span>
                </span>
              )}
            </div>
          )}
          </>)}

          {/* Lines */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span className="form-label" style={{ margin: 0 }}>Lines</span>
              <button className="btn btn-secondary" onClick={addLine} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}><IconSystem.add size={12} /> Add line</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '0.3rem', width: 120 }}>Type</th><th style={{ padding: '0.3rem', width: 220 }}>Item</th>
                <th style={{ padding: '0.3rem', width: 70 }}>Qty</th><th style={{ padding: '0.3rem', width: 90 }}>Rate</th>
                <th style={{ padding: '0.3rem', width: 110, textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '0.3rem', width: 90, textAlign: 'right' }}>Tax</th>
                <th style={{ padding: '0.3rem', width: 120, textAlign: 'right' }}>Total</th><th style={{ width: 30 }} />
              </tr></thead>
              <tbody>
                {lines.map(l => {
                  const lt = taxByKey[l.key];
                  const amt = parseFloat(l.lineAmount) || 0;
                  const isItem = allowItems && l.lineType === 'ITEM';
                  // SERVICE and ITEM rows share the one Service / Item picker (invoices only).
                  const combinedPicker = allowItems && (l.lineType === 'SERVICE' || l.lineType === 'ITEM');
                  const resolvedLedger = resolvedLedgerOf(l);
                  const freeText = isFreeTextService(l);
                  // A free-text line shows its ledger picker once something is typed on it.
                  const showFreeTextLedger = freeText && (!!l.description.trim() || parseFloat(l.lineAmount) > 0 || !!l.incomeLedgerId);
                  return (
                  <React.Fragment key={l.key}>
                    <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.25rem' }}><SyutSelect options={allowItems ? LINE_TYPE_OPTIONS_WITH_ITEMS : LINE_TYPE_OPTIONS} value={l.lineType === 'ITEM' ? 'SERVICE' : l.lineType} onChange={v => {
                        const next = (v as string) || 'SERVICE';
                        if (next === (l.lineType === 'ITEM' ? 'SERVICE' : l.lineType)) return;
                        // Type switch invalidates the current item selection —
                        // Service/Reimbursement items come from different
                        // catalogs, and Discount has no item at all. Clear
                        // serviceId + description + rate + lineAmount so the
                        // row starts fresh for pricing. Item + Rate + Amount
                        // are logically coupled (Amount = Qty × Rate), so a
                        // dangling Amount with a blank Rate would just be
                        // stale/inconsistent. Tax and Total re-derive from
                        // Amount (both go to 0/—). Only Qty survives — it's
                        // the one field the user typically wants to reuse
                        // across a Type switch.
                        // An ITEM row leaving for another type drops its item half too.
                        updLine(l.key, { lineType: next, serviceId: '', description: '', rate: '', lineAmount: '', incomeLedgerId: '', ...blankItemFieldValues() });
                      }} isClearable={false} size="sm" menuPortalTarget={document.body} /></td>
                      <td style={{ padding: '0.25rem' }}>{combinedPicker ? (
                        <SyutSelect key="product" isAsync loadOptions={loadServicesAndItems} defaultOptions cacheOptions={false}
                          seedOptions={isItem
                            ? (l.itemId ? [{ value: `${ITEM_PREFIX}${l.itemId}`, label: `Item · ${l.itemText || ''}` }] : undefined)
                            : (l.serviceId ? [{ value: `${SERVICE_PREFIX}${l.serviceId}`, label: `Service · ${serviceCache.current.get(l.serviceId)?.name || ''}` }] : undefined)}
                          value={isItem ? (l.itemId ? `${ITEM_PREFIX}${l.itemId}` : '') : (l.serviceId ? `${SERVICE_PREFIX}${l.serviceId}` : '')}
                          onChange={v => onPickProduct(l, (v as string) || '')} isClearable size="sm" menuPortalTarget={document.body}
                          placeholder="Pick service or item" error={errors[`line-${l.key}-item`]} />
                      ) : l.lineType !== 'DISCOUNT' ? <SyutSelect key={l.lineType} isAsync loadOptions={(input: string) => loadServices(input, l.lineType === 'REIMBURSEMENT' ? 'REIMBURSEMENT' : 'SERVICE')} defaultOptions cacheOptions={false}
                        seedOptions={l.serviceId ? [{ value: l.serviceId, label: serviceCache.current.get(l.serviceId)?.name || '' }] : undefined}
                        value={l.serviceId} onChange={v => onPickService(l.key, (v as string) || '')} isClearable size="sm" menuPortalTarget={document.body} placeholder={l.lineType === 'REIMBURSEMENT' ? 'Pick reimbursement' : 'Pick service'} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ padding: '0.25rem' }}><input className={`form-input${errors[`line-${l.key}-quantity`] ? ' form-field-error' : ''}`} style={{ fontSize: '0.8125rem' }} type="number" value={l.quantity} title={isItem && l.uomCode ? `In ${l.uomCode}` : undefined} placeholder={isItem ? 'Qty *' : undefined} onChange={e => updLine(l.key, { quantity: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem' }} type="number" value={l.rate} title={isItem && l.uomCode ? `Per ${l.uomCode}` : undefined} onChange={e => updLine(l.key, { rate: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem', textAlign: 'right' }} type="number" value={l.lineAmount} onChange={e => updLine(l.key, { lineAmount: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem', textAlign: 'right', color: 'var(--text-secondary)' }}>{lt ? money(lt.tax) : '—'}</td>
                      <td style={{ padding: '0.25rem', textAlign: 'right', fontWeight: 600 }}>{lt ? money(lt.total) : money(amt)}</td>
                      <td style={{ textAlign: 'center' }}><button title="Remove" onClick={() => removeLine(l.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}><IconSystem.x size={14} /></button></td>
                    </tr>
                    {isItem && l.itemId && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <ItemLineDetails row={l} lookups={itemLookups} onChange={p => updLine(l.key, p)}
                            ledger={<ReadOnlyLedgerValue ledger={resolvedLedger} missingText={ITEM_INCOME_LEDGER_MISSING}
                              title="Follows the masters — the item's / category's Sales income override, else the entity default" />}
                            ledgerLabel="Income ledger"
                            locationHint={onHandHint(l)}
                            errors={{ uom: errors[`line-${l.key}-uom`], location: errors[`line-${l.key}-location`] }} />
                        </td>
                      </tr>
                    )}
                    {l.lineType === 'SERVICE' && (l.serviceId || showFreeTextLedger) && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'start', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                            <div style={{ width: 300 }}>
                              {freeText ? (<>
                                <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>Income ledger <span className="text-danger">*</span></label>
                                <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                                  seedOptions={l.incomeLedgerId ? [{ value: l.incomeLedgerId, label: ledgerCache.current.get(l.incomeLedgerId) || '' }] : undefined}
                                  value={l.incomeLedgerId} onChange={v => updLine(l.key, { incomeLedgerId: (v as string) || '' })}
                                  isClearable size="sm" menuPortalTarget={document.body} placeholder="Pick the income ledger" error={errors[`line-${l.key}-ledger`]} />
                                <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>No service picked — this line names its own ledger</div>
                              </>) : (<>
                                <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>Income ledger</label>
                                <ReadOnlyLedgerValue ledger={resolvedLedger} missingText={SERVICE_INCOME_LEDGER_MISSING}
                                  title="Follows the masters — the service's / service category's Sales income ledger, else the entity default" />
                              </>)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={8} style={{ padding: '0 0.25rem 0.5rem 0.5rem' }}>
                        <textarea className="form-textarea" rows={descFocusKey === l.key ? 4 : 2}
                          style={{ fontSize: '0.8rem', minHeight: descFocusKey === l.key ? '4.5rem' : '2.9rem', width: '100%', transition: 'min-height 0.12s ease' }}
                          value={l.description} placeholder={isItem ? 'Description… (blank = the item name)' : 'Description…'}
                          onFocus={() => setDescFocusKey(l.key)} onBlur={() => setDescFocusKey(null)}
                          onChange={e => updLine(l.key, { description: e.target.value })} />
                      </td>
                    </tr>
                    {unmappedKeys.includes(l.key) && (isItem ? !!l.itemId : !!l.serviceId) && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 0.25rem 0.5rem 0.5rem' }}>
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>
                            {isItem
                              ? <>{l.itemText || 'This item'} has no tax mapping under {taxContext?.jurisdiction?.jurisdictionCode || 'the jurisdiction'}. Map its tax on the item, or pick another item; the document cannot be posted until then.</>
                              : <>{serviceCache.current.get(l.serviceId)?.name || 'This service'} has no tax master under {taxContext?.jurisdiction?.jurisdictionCode || 'the jurisdiction'}. Map it under Service Master, or pick another service; the document cannot be posted until then.</>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {errors.lines && <div className="form-error-message">{errors.lines}</div>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Narration</label>
              <textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} />
            </div>
            <div style={{ minWidth: 240, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>{hasItemLines ? 'Subtotal' : 'Fees subtotal'}</span><strong>{money(subtotal)}</strong></div>
              {reimbursementTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Reimbursements</span><strong>{money(reimbursementTotal)}</strong></div>}
              {discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-muted)' }}><span>Discount</span><span>− {money(discountTotal)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax</span><strong>{taxNote ? '—' : money(tax ? tax.tax : 0)}</strong></div>
              {taxNote && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', padding: '0 0 0.2rem' }}>{taxNote}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Grand Total</span><strong>{currencyCode} {money(grandTotal)}</strong></div>
              {currencyCode && currencyCode !== functionalCurrency && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  <span>≈ {functionalCurrency} @ {exchangeRate || '—'}</span>
                  <span>{functionalCurrency} {money(grandTotal * (parseFloat(exchangeRate) || 0))}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          {/* Edit-mode no-op guard — per ADR-0020 "edit = cancel + re-issue",
              a Save on an unchanged form would burn a fresh invoice number
              (BC14825 → BC14826) and churn a CANCELLED row for zero benefit.
              Disable the button while nothing is dirty; the affordance itself
              communicates "nothing to save" without needing a toast. Create
              mode always leaves the button enabled (user needs it to trigger
              validation errors on empty forms). */}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (!!editId && !isDirty)} title={editId && !isDirty ? 'No changes to save' : undefined}><IconSystem.check size={16} /> {editId ? 'Save Changes' : isCopy ? 'Post New Invoice' : `Post ${docLabel}`}</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={editId ? 'Save Changes' : isCopy ? 'Post New Invoice' : `Post ${docLabel}`}
        message={editId
          ? `Re-post this ${docLabel.toLowerCase()} for ${currencyCode} ${money(grandTotal)}? The ledger entries will be updated.`
          : isCopy
          ? `Post this as a new invoice for ${currencyCode} ${money(grandTotal)}? The source invoice ${invoice?.invoiceNumber || ''} is not affected.`
          : isDebitNote
          ? `Post this debit note for ${currencyCode} ${money(grandTotal)}? It adds to what the customer owes${againstInvoiceId ? ` on invoice ${againstLabelCache.current.get(againstInvoiceId)?.split(' · ')[0] || ''}` : ''}, as its own bill.`
          : `Post this invoice for ${currencyCode} ${money(grandTotal)}? It will be committed to the ledger.`}
        confirmLabel={editId ? 'Save' : 'Post'} cancelLabel="Cancel" variant="default" onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard changes?"
        message="You have unsaved changes on this invoice. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

// ---- View modal ----
interface InvoiceViewModalProps {
  invoice: SalesInvoiceDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (invoiceNo: string) => void;
}
export const InvoiceViewModal: React.FC<InvoiceViewModalProps> = ({ invoice, onClose, tabId, moduleName, onRestoreView }) => {
  const isDebitNote = invoice?.documentKind === 'DEBIT_NOTE';
  const { minimize } = useModalMinimize();
  const [billTo, setBillTo] = useState<PartyDto | null>(null);
  useEffect(() => {
    setBillTo(null);
    if (!invoice?.partyId) return;
    let active = true;
    partyService.getById(invoice.partyId).then(p => { if (active) setBillTo(p); }).catch(() => {});
    return () => { active = false; };
  }, [invoice?.partyId, invoice?.id]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !invoice) return;
    const invNo = invoice.invoiceNumber || invoice.id;
    minimize({
      // A debit note shares a register (and number shapes) with credit notes — keep its ids apart.
      id: `${tabId}-view-modal-${isDebitNote ? 'dn-' : ''}${invNo}`,
      title: `View - ${isDebitNote ? 'Debit Note ' : ''}${invNo}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(invoice.invoiceNumber || ''),
    });
    onClose();
  }, [tabId, moduleName, invoice, minimize, onClose, onRestoreView]);

  if (!invoice) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{isDebitNote ? 'Sales Debit Note' : 'Invoice'} {invoice.invoiceNumber}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Customer</div><strong>{invoice.partyName}</strong>{invoice.attentionTo ? <div style={{ color: 'var(--text-secondary)' }}>Attn: {invoice.attentionTo}</div> : null}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div>{(invoice.invoiceDate || '').slice(0, 10)}{invoice.dueDate ? <div style={{ color: 'var(--text-secondary)' }}>Due {(invoice.dueDate || '').slice(0, 10)}</div> : null}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Supply</div>{invoice.supplyType}{invoice.placeOfSupplyCode ? <div style={{ color: 'var(--text-secondary)' }}>Place of supply {invoice.placeOfSupplyCode}</div> : null}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div>{statusBadges(invoice)}</div>
          </div>
          {isDebitNote && (
            <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem' }}>
              <span><span style={{ color: 'var(--text-muted)' }}>Revises </span><strong>{invoice.againstInvoiceNumber || 'No invoice (stand-alone)'}</strong></span>
              {invoice.noteReason && <span><span style={{ color: 'var(--text-muted)' }}>Reason </span>{invoice.noteReason}</span>}
            </div>
          )}
          {/* Bill To — full contact block for review before printing/sending */}
          {billTo && (
            <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.8rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.15rem' }}>Bill To</div>
              <div>{billTo.partyName}{billTo.legalName && billTo.legalName !== billTo.partyName ? ` (${billTo.legalName})` : ''}</div>
              {formatAddress(billTo) && <div style={{ color: 'var(--text-secondary)' }}>{formatAddress(billTo)}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                {invoice.attentionTo && <span>Attn: {invoice.attentionTo}</span>}
                {billTo.email && <span>✉ {billTo.email}</span>}
                {billTo.phone && <span>☎ {billTo.phone}</span>}
                {billTo.creditDays ? <span>Credit: {billTo.creditDays}d</span> : null}
              </div>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem', width: 30 }}>#</th><th style={{ padding: '0.3rem', width: 120 }}>Type</th><th style={{ padding: '0.3rem' }}>Item</th><th style={{ padding: '0.3rem', textAlign: 'right', width: 120 }}>Amount</th><th style={{ padding: '0.3rem', textAlign: 'right', width: 110 }}>Tax</th></tr></thead>
            <tbody>{invoice.lines.map(l => (
              <React.Fragment key={l.id}>
                <tr style={{ borderTop: '1px solid var(--border-color)' }}><td style={{ padding: '0.3rem' }}>{l.lineNo}</td><td style={{ padding: '0.3rem' }}>{l.lineType}</td><td style={{ padding: '0.3rem' }}>{l.lineType === 'ITEM' ? <ItemLineSummary l={l} /> : (l.serviceName || '—')}</td><td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.lineAmount)}</td><td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.taxAmount)}</td></tr>
                {l.description && <tr><td /><td colSpan={4} style={{ padding: '0 0.3rem 0.4rem 0.3rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{l.description}</td></tr>}
                {snapshotSummary(l.taxSnapshot) && <tr><td /><td colSpan={4} style={{ padding: '0 0.3rem 0.4rem 0.3rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>{snapshotSummary(l.taxSnapshot)}</td></tr>}
              </React.Fragment>
            ))}</tbody>
          </table>
          <div style={{ marginLeft: 'auto', maxWidth: 260, marginTop: '0.75rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>{invoice.lines.some(l => l.lineType === 'ITEM') ? 'Subtotal' : 'Fees subtotal'}</span><strong>{money(invoice.subtotal)}</strong></div>
            {invoice.reimbursementTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Reimbursements</span><strong>{money(invoice.reimbursementTotal)}</strong></div>}
            {invoice.discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Discount</span><span>− {money(invoice.discountTotal)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax</span><strong>{money(invoice.taxTotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Grand Total</span><strong>{invoice.currencyCode} {money(invoice.grandTotal)}</strong></div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </>
  );
};

// ---- Screen ----
type InvoiceViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const INVOICE_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'invoiceNumber', label: 'Invoice No.' },
  { id: 'invoiceDate', label: 'Date' },
  { id: 'partyName', label: 'Customer' },
  { id: 'grandTotal', label: 'Total' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface SalesInvoiceScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const SalesInvoiceScreen: React.FC<SalesInvoiceScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('sales-invoice');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  // Debit notes live in the Credit / Debit Notes register (ADR-0053); this list is invoices.
  const [queryParams, setQueryParams] = useState<SalesInvoiceQueryParams>({ page: 1, pageSize: 25, documentKind: 'INVOICE' });
  const { data, loading, pagination, refetch } = useSalesInvoices(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<InvoiceViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('sales-invoice-columns', INVOICE_COLUMN_DEFS);

  const [filterOpen, setFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  // `editInvoice` also carries the SOURCE invoice in copy mode (name kept for
  // continuity; the modal receives it as `invoice` and treats it as a seed).
  const [editInvoice, setEditInvoice] = useState<SalesInvoiceDto | null>(null);
  const [viewInvoice, setViewInvoice] = useState<SalesInvoiceDto | null>(null);
  // "Raise debit note" (ADR-0053) opens the same form in debit-note mode, seeded from the invoice.
  const [formKind, setFormKind] = useState<SalesDocumentKind>('INVOICE');
  const [revisesInvoice, setRevisesInvoice] = useState<SalesInvoiceDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleView = useCallback(async (r: SalesInvoiceDto) => {
    // Restore-on-reopen: if this invoice's view is already minimized, restore
    // it instead of opening a second one.
    const viewId = tabId ? `${tabId}-view-modal-${r.invoiceNumber || r.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    try {
      setViewInvoice(await salesInvoiceService.getById(r.id));
    } catch {
      notificationService.toast.error('Could not load the invoice.');
    }
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // The setup gate (ADR-0048 §3): the form does not open until the entity can invoice; the
  // server refuses the post with the same list, so this is the friendly half.
  const setupGate = useSetupGate('INVOICING');

  const handleAdd = useCallback(async () => {
    if (!(await setupGate.ensureReady())) return;
    // Auto-minimize any open modal so the new Create draft starts fresh.
    if (formOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    setCreateInstanceId(String(next));
    setFormKind('INVOICE'); setRevisesInvoice(null);
    setFormMode('create'); setEditInvoice(null); setFormOpen(true);
  }, [formOpen, tabId, isMinimized, setupGate]);

  const handleRaiseDebitNote = useCallback(async (r: SalesInvoiceDto) => {
    if (!(await setupGate.ensureReady())) return;
    if (formOpen) formModalMinimizeRef.current?.();
    try {
      const full = await salesInvoiceService.getById(r.id);
      let next = 1;
      if (tabId) while (isMinimized(`${tabId}-form-modal-create-dn-${next}`)) next++;
      setCreateInstanceId(String(next));
      setFormKind('DEBIT_NOTE'); setRevisesInvoice(full);
      setFormMode('create'); setEditInvoice(null); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not load the invoice.');
    }
  }, [formOpen, tabId, isMinimized, setupGate]);

  const handleEdit = useCallback(async (r: SalesInvoiceDto) => {
    const blocked = editBlockReason(r);
    if (blocked) {
      notificationService.toast.warning(`This invoice cannot be edited — ${blocked}.`);
      return;
    }
    if (!(await setupGate.ensureReady())) return;
    // Restore-on-reopen: if this invoice's edit modal is already minimized,
    // restore that draft instead of opening a fresh Edit.
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.invoiceNumber || r.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    try {
      const full = await salesInvoiceService.getById(r.id);
      const stillBlocked = editBlockReason(full);
      if (stillBlocked) {
        notificationService.toast.warning(`This invoice cannot be edited — ${stillBlocked}.`);
        return;
      }
      setEditInvoice(full); setFormMode('edit'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not load the invoice.');
    }
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // Cross-screen drill-in (e.g. from the Item-wise Sales report): open a specific
  // invoice by id. Edit when allowed, else fall back to read-only view. The caller
  // has already checked the invoice belongs to the active entity/BU.
  const handleOpenById = useCallback(async (id: string, mode?: 'edit' | 'view') => {
    try {
      const full = await salesInvoiceService.getById(id);
      if (mode === 'view') { setViewInvoice(full); return; }
      const blocked = editBlockReason(full);
      if (blocked) {
        setViewInvoice(full);
        notificationService.toast.info(`Opened read-only — ${blocked}.`);
        return;
      }
      setEditInvoice(full); setFormMode('edit'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not open the invoice.');
    }
  }, []);

  useNavTarget('sales-invoice', (t) => handleOpenById(t.recordId, t.mode));

  const handleCopy = useCallback(async (r: SalesInvoiceDto) => {
    // Auto-minimize the open modal so the new Copy draft starts fresh.
    if (formOpen) formModalMinimizeRef.current?.();
    try {
      const full = await salesInvoiceService.getById(r.id);
      let next = 1;
      if (tabId) while (isMinimized(`${tabId}-form-modal-copy-${next}`)) next++;
      setCopyInstanceId(String(next));
      setEditInvoice(full); setFormMode('copy'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not load the source invoice.');
    }
  }, [formOpen, tabId, isMinimized]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, invoiceNo?: string, kind?: SalesDocumentKind) => {
    setFormKind(kind || 'INVOICE');
    if (mode === 'edit') {
      if (invoiceNo) {
        const rec = data.find(r => r.invoiceNumber === invoiceNo);
        if (rec) setEditInvoice(rec);
      }
      setFormMode('edit');
    } else if (mode === 'copy') {
      if (instanceId) setCopyInstanceId(instanceId);
      if (invoiceNo) {
        const rec = data.find(r => r.invoiceNumber === invoiceNo);
        if (rec) setEditInvoice(rec);
      }
      setFormMode('copy');
    } else {
      if (instanceId) setCreateInstanceId(instanceId);
      setFormMode('create');
    }
    setFormOpen(true);
  }, [data]);

  const handleRestoreView = useCallback((invoiceNo: string) => {
    const rec = data.find(r => r.invoiceNumber === invoiceNo);
    if (rec) setViewInvoice(rec);
  }, [data]);

  const handleDelete = useCallback(async (r: SalesInvoiceDto) => {
    const label: string = r.invoiceNumber || r.partyName || 'this invoice';
    const ok = await confirmDelete({ resourceLabel: label });
    if (!ok) return;
    try {
      await salesInvoiceService.remove(r.id, r.rowVersion);
      notificationService.toast.success(`Invoice ${r.invoiceNumber || ''} deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete the invoice.');
      if (err?.response?.status === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<SalesInvoiceDto>[] = useMemo(() => [
    {
      id: 'invoiceNumber', header: 'Invoice No.', accessorKey: 'invoiceNumber', size: 140,
      cell: ({ row }) => (row.original as SalesInvoiceDto).invoiceNumber || '—',
    },
    {
      id: 'invoiceDate', header: 'Date', accessorKey: 'invoiceDate', size: 110,
      cell: ({ row }) => ((row.original as SalesInvoiceDto).invoiceDate || '').slice(0, 10),
    },
    { id: 'partyName', header: 'Customer', accessorKey: 'partyName', size: 240 },
    {
      id: 'grandTotal', header: 'Total', accessorKey: 'grandTotal', size: 140, align: 'right',
      cell: ({ row }) => {
        const r = row.original as SalesInvoiceDto;
        return <span>{r.currencyCode} {money(r.grandTotal)}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'docStatus', size: 200,
      cell: ({ row }) => statusBadges(row.original as SalesInvoiceDto),
    },
  ], []);

  const rowActions = useCallback((r: SalesInvoiceDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
      if (r.docStatus === 'POSTED') {
        items.push({ id: 'debit-note', label: 'Raise debit note', icon: <IconSystem.add size={18} />, onClick: () => handleRaiseDebitNote(r) });
      }
    }
    if (isInvoiceEditable(r)) {
      if (can('UPDATE')) {
        items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      }
      if (can('DELETE')) {
        items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
      }
    }
    return items;
  }, [can, handleView, handleEdit, handleDelete, handleCopy, handleRaiseDebitNote]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'New Invoice'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const applyFilters = () => {
    setQueryParams(p => ({
      ...p, fromDate: fromDate || undefined, toDate: toDate || undefined,
      includeCancelled: includeCancelled || undefined, page: 1,
    }));
    setFilterOpen(false);
  };
  const clearFilters = () => {
    setFromDate(''); setToDate(''); setIncludeCancelled(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize, documentKind: 'INVOICE' }));
  };

  const appliedCount = [fromDate, toDate, includeCancelled ? '1' : ''].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Service Invoices"
      description="Post service invoices to the ledger"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search number / customer...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as InvoiceViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={INVOICE_COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'invoiceNumber']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No invoices yet — click New Invoice.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {r.invoiceNumber || '—'} · {(r.invoiceDate || '').slice(0, 10)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.partyName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{
                    fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex',
                    justifyContent: 'space-between', gap: '0.5rem', paddingTop: '0.4rem',
                    borderTop: '1px solid var(--border-color)', alignItems: 'center',
                  }}>
                    {statusBadges(r)}
                    <strong>{r.currencyCode} {money(r.grandTotal)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SetupGateModal gate={setupGate} />
      <InvoiceFormModal isOpen={formOpen} mode={formMode} invoice={editInvoice}
        documentKind={formKind} revises={revisesInvoice}
        onSaved={refetch} onClose={() => { setFormOpen(false); setEditInvoice(null); setFormMode('create'); setFormKind('INVOICE'); setRevisesInvoice(null); }}
        tabId={tabId} moduleName={moduleName || 'Service Invoices'}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />

      <InvoiceViewModal invoice={viewInvoice} onClose={() => setViewInvoice(null)}
        tabId={tabId} moduleName={moduleName || 'Service Invoices'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default SalesInvoiceScreen;
